const DEFAULTS = {
	// Debounce before a content/title edit is saved.
	saveDebounceMs: 500,
	// How long the pointer must rest on an element before its tooltip opens. 300-500ms is the
	// range design systems settle on: long enough that crossing an element does not fire it,
	// short enough not to feel broken. E2E sets this to 0 -- it asserts the rendered tooltip,
	// not the passage of a human-facing wait.
	tooltipDelayMs: 500,
	// Same, for small elements inside dense lists (dates, authors, table handles):
	// the pointer crosses them all the time while the user scans the list.
	tooltipLongDelayMs: 1000,
	// Window after a tooltip closes during which the next one opens without waiting again.
	tooltipSkipDelayMs: 300,
	// Tooltips always hide immediately; kept named so call sites carry no bare numbers.
	tooltipHideDelayMs: 0,
};

export type TimingKey = keyof typeof DEFAULTS;

const cache = new Map<TimingKey, number>();

// e2e can override a key via localStorage ("gx-timing-<key>"). There is no e2e profile: the
// defaults below are what the suite runs on, and an override is a per-test opt-in for scenarios
// that target a timing window (see autosave.spec).
// Read on first use, not at module init, so an override set right before a goto/reload always
// applies: the load starts a fresh module instance with an empty cache. Overrides are load-time
// only by design -- a setItem without a reload has no effect (every caller already reloads).
// Cached because timing() sits in render paths (useDebounce deps in the editor renderers), and
// localStorage is a synchronous API not worth touching on every render.
// catch also covers SSR, where `window` is undeclared and the read throws ReferenceError.
export const timing = (key: TimingKey): number => {
	const cached = cache.get(key);
	if (cached !== undefined) return cached;

	let value = DEFAULTS[key];
	try {
		const raw = window.localStorage.getItem(`gx-timing-${key}`);
		const override = raw === null ? NaN : Number(raw);
		if (Number.isFinite(override) && override >= 0) value = override;
	} catch {}

	cache.set(key, value);
	return value;
};

// Tiers a component may forward as a named prop. `satisfies` is what makes the mapping safe: rename
// a key in DEFAULTS and this errors here, at the definition, instead of rippling out to call sites.
const TIERS = {
	standard: "tooltipDelayMs",
	long: "tooltipLongDelayMs",
} as const satisfies Record<string, TimingKey>;

export type TooltipDelayTier = keyof typeof TIERS;

export const delayOf = (tier: TooltipDelayTier): number => timing(TIERS[tier]);

/**
 * Reads like a constant at the call site. A getter rather than a `const` only because e2e overrides
 * the value through localStorage, which a module-init constant would capture too early.
 */
export const tooltipDelay = {
	get standard(): number {
		return delayOf("standard");
	},
	get long(): number {
		return delayOf("long");
	},
	get skip(): number {
		return timing("tooltipSkipDelayMs");
	},
	get hide(): number {
		return timing("tooltipHideDelayMs");
	},
};
